import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Inbox, CheckCircle2, Clock, AlertTriangle, Send, RefreshCw, Search,
  Zap, ExternalLink, Mail, Building2,
} from "lucide-react";

/**
 * /app/leads — Admin leads inbox (hq_admin only).
 *
 * Shows every /register submission with delivery status to Work. Lets
 * ops redispatch stuck leads (pending_delivery / rejected) one at a
 * time or in a bulk sweep. Mirrors the GET /api/public/leads payload
 * shape so Work's status badges stay honest.
 */

const STATUS_META = {
  delivered: { label: "Delivered", icon: CheckCircle2, tone: "border-[#CCFF00]/40 bg-[#CCFF00]/[0.06] text-[#CCFF00]" },
  pending_delivery: { label: "Pending", icon: Clock, tone: "border-[#E8D6A3]/40 bg-[#E8D6A3]/[0.06] text-[#E8D6A3]" },
  rejected: { label: "Rejected", icon: AlertTriangle, tone: "border-red-400/40 bg-red-500/[0.05] text-red-300" },
  queued: { label: "Queued", icon: Clock, tone: "border-white/15 bg-white/[0.03] text-white/60" },
};

const TYPE_META = {
  advertiser: { label: "Advertiser", color: "text-[#CCFF00]" },
  agency: { label: "Agency", color: "text-[#00E5FF]" },
  venue: { label: "Venue", color: "text-[#E8D6A3]" },
  media_owner: { label: "Media owner", color: "text-[#D4B878]" },
  operator: { label: "Operator", color: "text-fuchsia-300" },
  other: { label: "Other", color: "text-white/60" },
};

export default function LeadsInbox() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all"); // all | pending_delivery | delivered | rejected
  const [query, setQuery] = useState("");
  const [redispatchBusy, setRedispatchBusy] = useState(false);
  const [perRowBusy, setPerRowBusy] = useState({});

  const load = async () => {
    setLoading(true);
    try {
      const params = filter !== "all" ? `?status=${filter}&limit=200` : "?limit=200";
      const r = await api.get(`/public/leads${params}`);
      setData(r.data);
    } catch (e) {
      toast.error(e?.response?.status === 403
        ? "Admin access required."
        : "Couldn't load leads.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line
  }, [filter]);

  const redispatchOne = async (leadId) => {
    setPerRowBusy((cur) => ({ ...cur, [leadId]: true }));
    try {
      const r = await api.post(`/public/leads/${leadId}/redispatch`);
      const s = r.data?.delivery?.status;
      if (s === "delivered") toast.success(`Delivered to Work · ${leadId.slice(0, 8)}`);
      else if (s === "pending_delivery") toast(`Still pending · ${leadId.slice(0, 8)}`);
      else if (s === "rejected") toast.error(`Rejected by Work · check payload`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Redispatch failed.");
    } finally {
      setPerRowBusy((cur) => ({ ...cur, [leadId]: false }));
    }
  };

  const redispatchAll = async () => {
    if (!window.confirm("Redispatch every pending/rejected lead to Work now?")) return;
    setRedispatchBusy(true);
    try {
      const r = await api.post("/public/leads/redispatch-batch?limit=100");
      toast.success(`Scanned ${r.data.scanned}, delivered ${r.data.delivered}.`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Batch redispatch failed.");
    } finally {
      setRedispatchBusy(false);
    }
  };

  const items = (data?.items || []).filter((row) => {
    if (!query.trim()) return true;
    const q = query.toLowerCase();
    return (
      (row.email || "").toLowerCase().includes(q) ||
      (row.name || "").toLowerCase().includes(q) ||
      (row.company || "").toLowerCase().includes(q)
    );
  });

  const stats = data?.stats || {};
  const stuckCount = (stats.pending_delivery || 0) + (stats.rejected || 0) + (stats.queued || 0);

  return (
    <div data-testid="leads-inbox" className="max-w-6xl mx-auto px-6 py-10">
      <div className="mb-8">
        <div className="text-[10px] uppercase tracking-[0.28em] text-[#D4B878] mb-2 flex items-center gap-2">
          <Inbox size={12} /> HQ · Public Leads Inbox
        </div>
        <h1 className="text-3xl sm:text-4xl font-semibold text-white tracking-tight">
          Everything coming off <span className="font-mono text-[#E8D6A3]">/register</span>
        </h1>
        <p className="mt-3 text-white/60 max-w-2xl">
          Every public submission is persisted here and dispatched to{" "}
          <span className="font-mono text-[#D4B878]">work.mirrorloops.com</span>. Stuck
          leads can be redispatched one-by-one or in a bulk sweep.
        </p>
      </div>

      {/* Config banner */}
      <div
        data-testid="leads-work-config"
        className={`mb-6 p-4 border rounded flex items-center justify-between gap-3 flex-wrap ${
          data?.work_configured
            ? "border-[#CCFF00]/30 bg-[#CCFF00]/[0.04]"
            : "border-red-400/40 bg-red-500/[0.05]"
        }`}
      >
        <div className="flex items-center gap-2">
          <Zap size={14} className={data?.work_configured ? "text-[#CCFF00]" : "text-red-300"} />
          <div className="text-sm">
            <div className="text-white font-semibold">
              {data?.work_configured ? "Work handoff live" : "Work handoff NOT configured"}
            </div>
            <div className="text-xs text-white/50 font-mono mt-0.5">
              {data?.work_base_url || "—"}
            </div>
          </div>
        </div>
        {!data?.work_configured && (
          <div className="text-xs text-red-200">
            Set <span className="font-mono">WORK_INTAKE_KEY</span> in backend <span className="font-mono">.env</span> to enable live delivery.
          </div>
        )}
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
        {[
          { k: "total", label: "Total", tone: "text-white" },
          { k: "delivered", label: "Delivered", tone: "text-[#CCFF00]" },
          { k: "pending_delivery", label: "Pending", tone: "text-[#E8D6A3]" },
          { k: "rejected", label: "Rejected", tone: "text-red-300" },
        ].map((s) => (
          <div
            key={s.k}
            data-testid={`leads-stat-${s.k}`}
            className="border border-white/10 rounded p-4 bg-white/[0.02]"
          >
            <div className="text-[10px] uppercase tracking-[0.22em] text-white/40">{s.label}</div>
            <div className={`text-3xl font-semibold mt-1 ${s.tone}`}>
              {(stats[s.k] || 0).toLocaleString()}
            </div>
          </div>
        ))}
      </div>

      {/* Controls */}
      <div className="flex items-center gap-2 mb-4 flex-wrap">
        {[
          { v: "all", label: "All" },
          { v: "pending_delivery", label: "Pending" },
          { v: "delivered", label: "Delivered" },
          { v: "rejected", label: "Rejected" },
        ].map((t) => (
          <button
            key={t.v}
            onClick={() => setFilter(t.v)}
            data-testid={`leads-filter-${t.v}`}
            className={`text-xs uppercase tracking-[0.18em] font-mono px-3 py-1.5 rounded-full border transition ${
              filter === t.v
                ? "border-[#D4B878]/60 bg-[#D4B878]/[0.08] text-white"
                : "border-white/10 bg-white/[0.02] text-white/60 hover:border-white/25"
            }`}
          >
            {t.label}
          </button>
        ))}
        <div className="flex-1" />
        <div className="relative">
          <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/30" />
          <Input
            data-testid="leads-search"
            placeholder="Search email, name, company…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="pl-8 bg-white/5 border-white/10 text-xs w-56"
          />
        </div>
        <Button
          onClick={load}
          disabled={loading}
          data-testid="leads-refresh"
          className="rounded-full border border-white/15 bg-transparent hover:bg-white/5 text-white text-xs"
        >
          <RefreshCw size={12} className={`mr-1 ${loading ? "animate-spin" : ""}`} />
          Refresh
        </Button>
        {stuckCount > 0 && (
          <Button
            onClick={redispatchAll}
            disabled={redispatchBusy || !data?.work_configured}
            data-testid="leads-batch-redispatch"
            className="rounded-full bg-[#CCFF00] hover:bg-[#B8E600] text-black font-semibold text-xs"
          >
            <Send size={12} className="mr-1" />
            {redispatchBusy ? "Redispatching…" : `Redispatch ${stuckCount} stuck`}
          </Button>
        )}
      </div>

      {/* Table */}
      <div className="border border-white/10 rounded-lg overflow-hidden bg-white/[0.02]">
        <div className="grid grid-cols-12 gap-3 px-4 py-3 border-b border-white/10 text-[10px] uppercase tracking-[0.18em] text-white/40 font-mono">
          <div className="col-span-3">Contact</div>
          <div className="col-span-2">Company</div>
          <div className="col-span-1">Type</div>
          <div className="col-span-2">Region</div>
          <div className="col-span-2">Status</div>
          <div className="col-span-2 text-right">Actions</div>
        </div>
        {loading && (
          <div className="p-10 text-center text-white/40 text-sm">Loading…</div>
        )}
        {!loading && items.length === 0 && (
          <div data-testid="leads-empty" className="p-10 text-center text-white/40">
            {query ? "No leads match your search." : "No leads to show."}
          </div>
        )}
        {items.map((row) => {
          const meta = STATUS_META[row.delivery_status] || STATUS_META.queued;
          const Icon = meta.icon;
          const type = TYPE_META[row.type] || TYPE_META.other;
          const stuck = ["pending_delivery", "rejected", "queued"].includes(row.delivery_status);
          const busy = perRowBusy[row.id];
          return (
            <div
              key={row.id}
              data-testid={`leads-row-${row.id}`}
              className="grid grid-cols-12 gap-3 px-4 py-3 border-b border-white/5 text-sm hover:bg-white/[0.02] items-center"
            >
              <div className="col-span-3 min-w-0">
                <div className="text-white font-medium truncate">{row.name}</div>
                <div className="text-[11px] text-white/50 font-mono flex items-center gap-1 truncate">
                  <Mail size={10} /> {row.email}
                </div>
              </div>
              <div className="col-span-2 min-w-0">
                <div className="text-white/80 text-xs flex items-center gap-1 truncate">
                  <Building2 size={10} /> {row.company || "—"}
                </div>
              </div>
              <div className="col-span-1">
                <span className={`text-[10px] uppercase tracking-[0.14em] font-mono ${type.color}`}>
                  {type.label}
                </span>
              </div>
              <div className="col-span-2 text-xs text-white/70 truncate">{row.region || "—"}</div>
              <div className="col-span-2">
                <span
                  data-testid={`leads-status-${row.id}`}
                  className={`inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-[0.14em] px-2 py-0.5 rounded border ${meta.tone}`}
                >
                  <Icon size={10} /> {meta.label}
                </span>
                {row.work_response_code && (
                  <div className="text-[10px] text-white/40 font-mono mt-0.5">
                    HTTP {row.work_response_code}
                  </div>
                )}
              </div>
              <div className="col-span-2 flex items-center justify-end gap-1">
                {stuck && (
                  <Button
                    onClick={() => redispatchOne(row.id)}
                    disabled={busy || !data?.work_configured}
                    data-testid={`leads-redispatch-${row.id}`}
                    className="rounded-full bg-[#D4B878]/90 hover:bg-[#E8D6A3] text-black text-[10px] font-semibold px-3 py-1 h-auto"
                  >
                    {busy ? "…" : "Redispatch"}
                  </Button>
                )}
                {row.delivery_status === "delivered" && (
                  <span className="text-[10px] text-white/30 font-mono">
                    {String(row.last_dispatched_at || "").slice(5, 16).replace("T", " ")}
                  </span>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Empty-state hint */}
      {!loading && (data?.stats?.total || 0) === 0 && (
        <div className="mt-6 text-xs text-white/40">
          Hint: submit a test lead via{" "}
          <a
            href="/register"
            target="_blank"
            rel="noreferrer"
            className="text-[#D4B878] underline inline-flex items-center gap-1"
          >
            /register <ExternalLink size={10} />
          </a>{" "}
          to see it land here.
        </div>
      )}
    </div>
  );
}
